import React from "react";
import { useDisclosure } from "../../../../../helpers";
import { useBreakpoint } from "../../../../../helpers/src/hooks/useBreakpoint";
import { DataManContext, defaultViewModeKey, viewModels } from "../defines";
import { buildGroupedData } from "../helpers/buildGroupedData";
import { buildHierarchyColumnsDefFromHierarchyData } from "../helpers/buildHierarchyColumnsDefFromHierarchyData";
import { buildHierarchyData } from "../helpers/buildHierarchyData";
import { getDataManCallbackOrTypeValue } from "../helpers/getDataManCallbackOrTypeValue";
import {
  DataManConfirmData,
  DataManConfirmDisclosureData
} from "../types/actions";
import {
  DataManProviderProps,
  DataManState,
  DataManStateOptions
} from "../types/state";

export const DataManProvider: React.FC<DataManProviderProps> = (props) => {
  const {
    // Attributes
    children,
    hierarchyData: _hierarchyData,
    hierarchyColumns: _hierarchyColumns,
    data: _data,
    columns: _columns,
    actions: _actions,
    hierarchyActions: _hierarchyActions,

    // Methods
    onRecordMoved,
    onRecordClick,
    onHierarchyRecordClick,

    // Load more props (need to be reactive)
    hasMoreData,
    onLoadMore,
    isLoadingMore,

    // Rest goes to initialState
    ...initialState
  } = props;

  const openConfirmationModal = useDisclosure<DataManConfirmDisclosureData>();

  // Optimistic data state for drag and drop - shows reordered list immediately
  // biome-ignore lint/suspicious/noExplicitAny: data can be any shape
  const [optimisticData, setOptimisticData] = React.useState<any[] | null>(
    null
  );
  // biome-ignore lint/suspicious/noExplicitAny: data can be any shape
  const [optimisticHierarchyData, setOptimisticHierarchyData] = React.useState<
    any[] | null
  >(null);

  // Selection state for bulk actions - use Set for O(1) lookups
  const [selectedItemsSet, setSelectedItemsSet] = React.useState<Set<string>>(
    new Set()
  );

  // Clear optimistic data when prop data changes (server sync complete)
  React.useEffect(() => {
    setOptimisticData(null);
  }, [_data]);

  // Clear optimistic hierarchy data when prop data changes (server sync complete)
  React.useEffect(() => {
    setOptimisticHierarchyData(null);
  }, [_hierarchyData]);

  // Use optimistic data during updates, otherwise use prop data
  const effectiveData = optimisticData ?? _data;
  const effectiveHierarchyData = optimisticHierarchyData ?? _hierarchyData;

  const [state, setState] = React.useState<DataManStateOptions>({
    ...initialState,
    viewMode: initialState.viewMode || defaultViewModeKey,
    enabledViewModes: initialState.enabledViewModes || [defaultViewModeKey],
    enableHierarchy: initialState.enableHierarchy || false,
    dataIdKey: initialState.dataIdKey || "id",
    hierarchyKey: initialState.hierarchyKey || "id",
    hierarchyParentKey: initialState.hierarchyParentKey || "parentId",
    hiddenHierarchyColumns: initialState.hiddenHierarchyColumns || [],
    hierarchyItemKey: initialState.hierarchyItemKey || "id",
    hiddenColumns: initialState.hiddenColumns || [],
    containerRef: initialState.containerRef || null,
    maxActionButtons: initialState.maxActionButtons || 3,
    allowBulkSelection: !!initialState.allowBulkSelection || false,
    allowClickToSelect: initialState.allowClickToSelect !== false,
    allowHierarchyBulkSelection:
      !!initialState.allowHierarchyBulkSelection || true,
    allowSortableDataColumns: !!initialState.allowSortableDataColumns || false,
    fullSize: !!initialState.fullSize || false,
    showSelectedBackground: initialState.showSelectedBackground !== false,
    enableVirtualization: !!initialState.enableVirtualization || false,
    virtualizationOptions: {
      rowHeight: initialState.virtualizationOptions?.rowHeight ?? 64,
      overscanCount: initialState.virtualizationOptions?.overscanCount ?? 5,
      height: initialState.virtualizationOptions?.height
    },
    isHierarchyCollapsable: !!initialState.isHierarchyCollapsable || true,
    openHierarchyItems: initialState.openHierarchyItems || [],
    hierarchyRecordClickAction: "Auto",
    maxHierarchyDepth: initialState.maxHierarchyDepth,
    // GroupBy options
    enableGroupBy: initialState.enableGroupBy || false,
    groupByKey: initialState.groupByKey,
    groups: initialState.groups,
    defaultGroup: initialState.defaultGroup,
    alwaysViewAsCompact: initialState.alwaysViewAsCompact || false
  });

  // Determine if we should use compact view based on breakpoint or alwaysViewAsCompact prop
  const { isBreakpoint } = useBreakpoint();
  const isMobileBreakpoint = isBreakpoint("base", "sm");
  const isCompactView = state.alwaysViewAsCompact || isMobileBreakpoint;

  // Use compact view mode when on mobile or when alwaysViewAsCompact is true
  const effectiveViewMode = isCompactView
    ? "compact"
    : state.viewMode || defaultViewModeKey;
  const viewMode = viewModels[effectiveViewMode];

  const sanitizedHierarchyData = JSON.stringify(effectiveHierarchyData);
  const sanitizedData = JSON.stringify(effectiveData);

  const hierarchyData = React.useMemo(() => {
    if (!state.enableHierarchy || !effectiveHierarchyData) return null;
    return buildHierarchyData(
      effectiveHierarchyData,
      effectiveData || [],
      state
    );
  }, [
    sanitizedHierarchyData,
    sanitizedData,
    state.enableHierarchy,
    state.hierarchyKey,
    state.hierarchyParentKey
  ]);

  // Build grouped data when groupBy is enabled
  const groupedData = React.useMemo(() => {
    if (!state.enableGroupBy || !effectiveData) return null;
    return buildGroupedData(effectiveData, state);
  }, [
    sanitizedData,
    state.enableGroupBy,
    state.groupByKey,
    state.groups,
    state.defaultGroup
  ]);

  const hasGroupBy = !!(state.enableGroupBy && state.groupByKey);

  const hierarchyColumns = React.useMemo(() => {
    if (!state.enableHierarchy) return [];
    if (_hierarchyColumns) return _hierarchyColumns;
    if (!effectiveHierarchyData) return [];

    return buildHierarchyColumnsDefFromHierarchyData(effectiveHierarchyData);
  }, [_hierarchyColumns, sanitizedHierarchyData]);

  const visibleHierarchyColumns = React.useMemo(() => {
    return hierarchyColumns.filter(
      (column) => !state.hiddenHierarchyColumns?.includes(column.key)
    );
  }, [hierarchyColumns]);

  const columns = React.useMemo(() => {
    if (_columns) return _columns;
    if (!effectiveData) return [];
    return buildHierarchyColumnsDefFromHierarchyData(effectiveData);
  }, [_columns, sanitizedData]);

  const visibleColumns = React.useMemo(() => {
    return columns.filter(
      (column) => !state.hiddenColumns?.includes(column.key)
    );
  }, [columns]);

  // Only inline-capable actions earn the row actions column; an action set
  // that is bulk-only (see DataManBulkActionsFooter) would otherwise leave an
  // empty column on every row.
  const hasActions = (_actions ?? []).some(
    (action) => action.allowInline !== false
  );
  const hasHierarchyActions = (_hierarchyActions?.length || 0) > 0;
  const hasBulkSelection = state.allowBulkSelection || false;
  const hasSortableDataColumns = state.allowSortableDataColumns || false;
  const isRecordClickable =
    !!onRecordClick || (hasBulkSelection && !!state.allowClickToSelect);

  const toggleHierarchyItem = (key: string, isOpen?: boolean) => {
    if (isOpen !== undefined) {
      setState((prevState) => {
        const openHierarchyItems = isOpen
          ? [...(prevState.openHierarchyItems || []), key]
          : (prevState.openHierarchyItems || []).filter((item) => item !== key);

        return {
          ...prevState,
          openHierarchyItems
        };
      });

      return;
    }

    setState((prevState) => {
      const openHierarchyItems = (prevState.openHierarchyItems || []).includes(
        key
      )
        ? (prevState.openHierarchyItems || []).filter((item) => item !== key)
        : [...(prevState.openHierarchyItems || []), key];

      return {
        ...prevState,
        openHierarchyItems
      };
    });
  };

  const moveRecord = async (
    recordIds: string[],
    source: string,
    target: string,
    sourceIndex: number,
    targetIndex: number
  ) => {
    const recordId = recordIds[0];

    // Check if this is a hierarchy item (draggableId ends with -)
    const isHierarchyItem = recordId.endsWith("-");

    if (isHierarchyItem) {
      // For hierarchy items, work with effectiveHierarchyData
      const currentData = effectiveHierarchyData || [];
      const reorderedData = [...currentData];
      const [movedItem] = reorderedData.splice(sourceIndex, 1);
      reorderedData.splice(targetIndex, 0, movedItem);

      // Set optimistic hierarchy data immediately for instant UI feedback
      setOptimisticHierarchyData(reorderedData);

      const event = {
        recordId: recordId.slice(0, -1), // Remove trailing -
        source,
        target,
        targetIndex,
        sourceIndex,
        record: movedItem
      };

      try {
        await onRecordMoved?.(event, reorderedData);
      } catch {
        // On error, revert to original data
        setOptimisticHierarchyData(null);
      }
      // Note: optimisticHierarchyData will be cleared automatically when _hierarchyData prop changes
      return;
    }

    // For regular data items
    const currentData = effectiveData || [];

    // Simple reorder: move item from sourceIndex to targetIndex
    const reorderedData = [...currentData];
    const [movedItem] = reorderedData.splice(sourceIndex, 1);
    reorderedData.splice(targetIndex, 0, movedItem);

    // Set optimistic data immediately for instant UI feedback
    setOptimisticData(reorderedData);

    const event = {
      recordId,
      source,
      target,
      targetIndex,
      sourceIndex,
      record: movedItem
    };

    try {
      // Call the callback and wait for it to complete
      await onRecordMoved?.(event, reorderedData);
    } catch {
      // On error, revert to original data
      setOptimisticData(null);
    }
    // Note: optimisticData will be cleared automatically when _data prop changes
  };

  // Selection methods - memoized for performance
  const idKey = state.dataIdKey || "id";

  const toggleSelectItem = React.useCallback((itemId: string) => {
    setSelectedItemsSet((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = React.useCallback(() => {
    if (!effectiveData) return;
    setSelectedItemsSet((prev) => {
      if (prev.size === effectiveData.length) {
        return new Set();
      }
      const allIds = effectiveData.map((item: Record<string, unknown>) =>
        String(item[idKey])
      );
      return new Set(allIds);
    });
  }, [effectiveData, idKey]);

  const clearSelection = React.useCallback(() => {
    setSelectedItemsSet(new Set());
  }, []);

  const isItemSelected = React.useCallback(
    (itemId: string) => {
      return selectedItemsSet.has(itemId);
    },
    [selectedItemsSet]
  );

  // Convert Set to array for consumers that need it
  const selectedItems = React.useMemo(
    () => Array.from(selectedItemsSet),
    [selectedItemsSet]
  );

  const isAllSelected = effectiveData
    ? effectiveData.length > 0 && selectedItemsSet.size === effectiveData.length
    : false;

  const dataManFinalState: DataManState = {
    // Props
    ...state,
    viewMode: effectiveViewMode,
    data: effectiveData,
    hierarchyData,
    groupedData,
    hasGroupBy,
    hierarchyColumns,
    visibleHierarchyColumns,
    columns,
    visibleColumns,
    viewModeOptions: viewMode,
    hasActions,
    hasHierarchyActions,
    actions: _actions,
    hierarchyActions: _hierarchyActions,
    hasBulkSelection,
    hasSortableDataColumns,
    isRecordClickable,
    selectedItems,
    isAllSelected,
    isCompactView,

    // Load more (reactive props)
    hasMoreData,
    onLoadMore,
    isLoadingMore,

    // Disclosures
    openConfirmationModal,

    // Methods
    toggleHierarchyItem,
    moveRecord,
    toggleSelectItem,
    toggleSelectAll,
    clearSelection,
    isItemSelected,
    clickRecord: (target) => {
      // Toggle selection when bulk selection is enabled and allowClickToSelect is true
      if (hasBulkSelection && state.allowClickToSelect) {
        const targetId = String(
          (target as Record<string, unknown>)?.[idKey] || ""
        );
        if (targetId) {
          toggleSelectItem(targetId);
        }
      }
      onRecordClick?.({
        state: dataManFinalState,
        target
      });
    },
    clickHierarchyRecord: (target) => {
      const allowCustomAction =
        state.hierarchyRecordClickAction === "All" ||
        state.hierarchyRecordClickAction === "Custom" ||
        (state.hierarchyRecordClickAction === "Auto" &&
          !!onHierarchyRecordClick);
      const allowToggleAction =
        state.hierarchyRecordClickAction === "All" ||
        state.hierarchyRecordClickAction === "Toggle" ||
        (state.hierarchyRecordClickAction === "Auto" &&
          !onHierarchyRecordClick);

      if (allowToggleAction) {
        toggleHierarchyItem(target.id);
      }

      if (allowCustomAction) {
        onHierarchyRecordClick?.({
          state: dataManFinalState,
          target
        });
      }
    },
    triggerAction: async (action, actionOptions, callback) => {
      let confirmData: DataManConfirmData | null | undefined;

      const runAction = async () => {
        await action.onAction?.({ ...actionOptions });
        callback(true);
      };

      if (action.confirm) {
        confirmData = await getDataManCallbackOrTypeValue(
          action.confirm,
          actionOptions
        );
      }
      if (confirmData) {
        const message = await getDataManCallbackOrTypeValue(
          confirmData.message,
          actionOptions
        );
        const title = await getDataManCallbackOrTypeValue(
          confirmData.title,
          actionOptions
        );
        const confirmButtonProps = await getDataManCallbackOrTypeValue(
          confirmData.confirmButtonProps,
          actionOptions
        );
        const cancelButtonProps = await getDataManCallbackOrTypeValue(
          confirmData.cancelButtonProps,
          actionOptions
        );

        openConfirmationModal.open({
          message,
          title,
          confirmButtonProps,
          cancelButtonProps,
          onCancel: () => {
            callback(false);
            openConfirmationModal.close();
          },
          onConfirm: async () => {
            await runAction();
            openConfirmationModal.close();
          },
          onClose: () => {
            callback(false);
          }
        });
        return;
      }
      await runAction();
    }
  };

  return (
    <DataManContext.Provider value={dataManFinalState}>
      {children}
    </DataManContext.Provider>
  );
};
